/**
 * Alle Benachrichtigungen (F-30), live aus der Datenbank: Schnappschuss der
 * Daten -> reine Regeln (`domain/benachrichtigungen`). Laeuft jede Minute
 * neu, damit Faelliges ohne Neuladen erscheint.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';

import { db } from '@/db/schema';
import { getOccurrences } from '@/db/repositories/events';
import { META_LAST_EXPORT } from '@/db/repositories/meta';
import { META_BENACHRICHTIGUNGEN, meldungenAufraeumen } from '@/db/repositories/meldungen';
import { collectDeadlines } from '@/domain/deadlines';
import { meilensteinItems } from '@/domain/projects/meilensteine';
import { einheitenAm, planAm } from '@/domain/training/plan';
import { STANDARD_MINDEST_MAHLZEITEN, zieleFuer } from '@/domain/ernaehrung/ueberblick';
import { trainingstage } from '@/db/repositories/ernaehrungUeberblick';
import { einstellungenAus, type Einstellungen } from '@/domain/benachrichtigungen/einstellungen';
import { meldungen, PLAN_TAGE, type Meldung, type Schnappschuss } from '@/domain/benachrichtigungen/regeln';
import type { MeldungsZustand } from '@/domain/benachrichtigungen/zustand';
import { addDays, isoWeekKey, now, toLocalDate, today } from '@/lib/datetime';
import type { HabitDays } from '@/domain/habits/daily';
import type { LocalDate, LocalDateTime } from '@/domain/types';
import { bundeslandAus, holidayMap } from '@/lib/holidays/de';

/** Minutentakt: `jetzt` auf die Minute genau. */
export function useJetzt(): LocalDateTime {
  const [jetzt, setJetzt] = useState(now);
  useEffect(() => {
    const t = setInterval(() => setJetzt(now()), 20_000);
    return () => clearInterval(t);
  }, []);
  return jetzt;
}

export function useBenachrichtigungsEinstellungen(): Einstellungen {
  const roh = useLiveQuery(async () => (await db.meta.get(META_BENACHRICHTIGUNGEN))?.value ?? null, [], null);
  return useMemo(() => einstellungenAus(roh), [roh]);
}

async function schnappschuss(heute: LocalDate): Promise<Omit<Schnappschuss, 'jetzt'>> {
  const bis = addDays(heute, PLAN_TAGE);
  const [termine, tasks, projects, goals, exams, habits, entries, journal, wochen, monate, sicherung, fokus, plaene, planEinheiten, protokolle, schule] = await Promise.all([
    getOccurrences({ from: `${addDays(heute, -8)}T00:00` as LocalDateTime, to: `${bis}T00:00` as LocalDateTime }),
    db.tasks.toArray(), db.projects.toArray(), db.goals.toArray(),
    db.events.filter((e) => !!e.klausur && String(e.start).slice(0, 10) >= heute).toArray(),
    db.habits.toArray(),
    db.habitEntries.where('date').between(addDays(heute, -1), bis, true, true).toArray(),
    db.journal.where('date').between(addDays(heute, -1), bis, true, true).toArray(),
    db.weeklyReviews.toArray(), db.monthlyReviews.toArray(),
    db.meta.get(META_LAST_EXPORT),
    db.focusGoals.where('[art+key]').equals(['woche', isoWeekKey(heute)]).toArray(),
    db.trainingsplaene.toArray(), db.planEinheiten.toArray(),
    db.trainingsprotokolle.where('datum').between(addDays(heute, -1), bis, true, true).toArray(),
    // Bundesland und Ferien fuer die freien Tage (F-41) - im Live-Query, damit Aenderungen sofort wirken.
    db.schoolSettings.get('schule'),
  ]);
  const ernaehrung = await ernaehrungHeute(heute);
  // Training (F-34 E2): geplante Einheiten je Tag und ob sie schon fertig sind.
  const training = Array.from({ length: PLAN_TAGE + 1 }, (_, i) => addDays(heute, i - 1)).map((tag) => {
    const plan = planAm(plaene, tag);
    return {
      tag,
      einheiten: (plan ? einheitenAm(plan, planEinheiten, tag) : []).map((e) => ({
        name: e.name, uhrzeit: e.uhrzeit,
        erledigt: protokolle.some((p) => p.planEinheitId === e.id && p.datum === tag && p.status === 'fertig'),
      })),
    };
  });
  const gewohnheiten: HabitDays[] = habits.map((habit) => ({
    habit, done: new Set(entries.filter((x) => x.habitId === habit.id && x.done).map((x) => x.date)),
  }));
  const export_ = typeof sicherung?.value === 'string' ? sicherung.value : null;
  return {
    heute, termine, aufgaben: tasks, gewohnheiten,
    deadlines: collectDeadlines({ tasks, projects, goals, exams }),
    meilensteine: meilensteinItems(projects),
    journal: new Set(journal.map((j) => `${j.date}:${j.type}`)),
    wochenReflexionen: new Set(wochen.map((w) => w.isoWeek)),
    monatsReflexionen: new Set(monate.map((m) => m.month)),
    letzteSicherung: export_ ? toLocalDate(new Date(export_)) : null,
    wochenziele: fokus.filter((f) => f.status === 'offen').sort((a, b) => a.order - b.order).map((f) => f.title),
    training,
    ...(ernaehrung ? { ernaehrung } : {}),
    // Ab gestern: die Regeln planen ab gestern (Ruhezeit-Ueberhang).
    feiertage: new Set(holidayMap(addDays(heute, -1), bis, bundeslandAus(schule?.bundesland)).keys()),
    ferien: schule?.ferien ?? [],
  };
}

/** Ernaehrung heute (F-35 E): nur gelesen, wenn eine der beiden Meldungen an ist. */
async function ernaehrungHeute(heute: LocalDate): Promise<Schnappschuss['ernaehrung']> {
  const e = einstellungenAus((await db.meta.get(META_BENACHRICHTIGUNGEN))?.value);
  if (!e.arten.trinken.an && !e.arten.ernaehrungEintragen.an) return undefined;
  const [ein, gegessen, getraenke, training] = await Promise.all([
    db.ernaehrungsEinstellungen.get('standard'), db.gegessen.get(heute), db.getraenke.where('datum').equals(heute).toArray(), trainingstage(heute, heute),
  ]);
  return {
    tag: heute, getrunkenMl: getraenke.reduce((s, g) => s + g.ml, 0), zielMl: zieleFuer(ein, training.has(heute)).wasserMl,
    mahlzeiten: gegessen?.vollstaendig ? Number.MAX_SAFE_INTEGER : (gegessen?.mahlzeiten.filter((m) => m.posten.length).length ?? 0),
    mindest: ein?.mindestMahlzeiten ?? STANDARD_MINDEST_MAHLZEITEN,
  };
}

export interface MeldungsDaten {
  alle: Meldung[];
  zustand: Map<string, MeldungsZustand>;
  einstellungen: Einstellungen;
  jetzt: LocalDateTime;
  geladen: boolean;
}

export function useMeldungen(): MeldungsDaten {
  const jetzt = useJetzt();
  const heute = jetzt.slice(0, 10);
  const einstellungen = useBenachrichtigungsEinstellungen();
  useEffect(() => { void meldungenAufraeumen(today()); }, [heute]);
  const daten = useLiveQuery(() => schnappschuss(heute), [heute]);
  const zustandListe = useLiveQuery(() => db.meldungen.toArray(), [], [] as MeldungsZustand[]);
  const alle = useMemo(() => (daten ? meldungen({ ...daten, jetzt }, einstellungen) : []), [daten, jetzt, einstellungen]);
  const zustand = useMemo(() => new Map(zustandListe.map((z) => [z.id, z])), [zustandListe]);
  return { alle, zustand, einstellungen, jetzt, geladen: daten !== undefined };
}
